import {
  applyRecurringChange,
  availableSlotsForDate,
  identityToMnemonic,
  occupancyLabels,
  dayHold,
  occurrencesBetween,
  occurrencesForDate,
  planDayBooking,
  readDayOccupancy,
  readOccupancy,
  type Gym,
  type MemberSchedule,
  type Occurrence,
  type OccupancyLevel,
  parseInviteLinkLocation,
  type OccupancyReading,
  type RecurringIntent,
  type Role,
} from "@palestra/core";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { formatBuildLine, formatRelayLine } from "./build-info";
import { useRegisterSW } from "./pwa";
import { detectCapabilities, requestPersistentStorage, storageDiagnostics } from "./capabilities";
import { fillClass } from "./capacity-fill";
import { coachEntryGranted } from "./coach-entry";
import { pathKeepingSim, simSessionId } from "./dev-session";
import { inviteFromLocation, inviteFromText, useAppStore, useSyncStatus } from "./app-store";
import {
  claimInviteLink,
  inviteLinkFromLocation,
  redeemInviteLink,
  type InviteLinkRedemption,
} from "./invite-link";
import { copyInviteLink, copyInviteQr, type CopyOutcome } from "./invite-share";
import {
  addPlainDateDays,
  capitalizeWords,
  dateInTimeZone,
  dayClassName,
  formatBytes,
  formatDateOnly,
  formatDay,
  formatTime,
  formatWeekday,
  instantAtZonedDateTime,
  isoWeekday,
  plainDateDay,
  shortWeekday,
  startOfIsoWeek,
  weekdayName,
} from "./format";
import { matchesWord, pickChallengeIndexes } from "./mnemonic-check";
import {
  syncBadgeLabel,
  syncErrorDetail,
  syncExplanation,
  syncNeedsAttention,
} from "./sync-status";
import { EmptyState, Modal, PageTitle } from "./ui";

/**
 * I chunk per ruolo (PERF-04): il pannello coach, l'editor degli orari e il
 * generatore di QR arrivano solo a chi li apre. L'atleta, che e' la maggioranza,
 * non li scarica al primo avvio.
 */
const CoachHome = lazy(() => import("./coach-home"));
const GymEditor = lazy(() => import("./gym-editor"));
const InviteQr = lazy(() => import("./invite-qr"));
const BroadcastPage = lazy(() => import("./broadcast"));

type Page = "home" | "planning" | "coach" | "broadcast" | "settings";

function initialPage(): Page {
  return new URLSearchParams(location.search).has("envelope") ? "broadcast" : "home";
}

/** `none` prima e dopo; `loading` mentre si chiede la cassetta al relay. */
type InviteLinkView = { kind: "none" } | { kind: "loading" } | InviteLinkRedemption;

/**
 * Il riscatto del link corto (§1.4): token dal percorso, chiave dal frammento,
 * blob da `GET /l/<token>`. Il frammento viene tolto dall'indirizzo con
 * `history.replaceState` appena usato — altrimenti la chiave resta nella
 * cronologia del browser — tranne quando il relay non ha risposto: lì il link
 * deve restare, perché ricaricare è il modo di riprovare.
 */
function useInviteLinkRedemption(): [InviteLinkView, () => void] {
  const [view, setView] = useState<InviteLinkView>(() =>
    inviteLinkFromLocation() ? { kind: "loading" } : { kind: "none" },
  );
  useEffect(() => {
    const link = inviteLinkFromLocation();
    if (!link) return;
    let active = true;
    void redeemInviteLink(link).then((outcome) => {
      if (outcome.kind !== "unreachable") history.replaceState({}, "", pathKeepingSim("/"));
      if (active) setView(outcome);
    });
    return () => {
      active = false;
    };
  }, []);
  const dismiss = useCallback(() => setView({ kind: "none" }), []);
  return [view, dismiss];
}

/**
 * Lo switcher delle sessioni simulate (`src/sim/switcher.tsx`), strumento di
 * sviluppo. Il ramo è costante e falso in produzione: la build elimina il
 * `import()` insieme al ramo, quindi il chunk non viene nemmeno emesso.
 */
const SimSwitcher = import.meta.env.DEV ? lazy(() => import("./sim/switcher")) : undefined;

export function App() {
  return (
    <>
      <AppScreens />
      {SimSwitcher && simSessionId() ? (
        <Suspense fallback={null}>
          <SimSwitcher />
        </Suspense>
      ) : null}
    </>
  );
}

function AppScreens() {
  const store = useAppStore();
  const capabilities = useMemo(detectCapabilities, []);
  const [page, setPage] = useState<Page>(initialPage);
  const pendingInvite = inviteFromLocation();
  const [inviteLink, dismissInviteLink] = useInviteLinkRedemption();

  if (capabilities.blocking.length > 0) {
    return (
      <>
        <CenteredCard title="Browser non compatibile">
          <p>Per proteggere i tuoi dati servono: {capabilities.blocking.join(", ")}.</p>
          <p>Aggiorna il browser o apri il link con Safari/Chrome recente.</p>
        </CenteredCard>
        <InstallAndUpdate />
      </>
    );
  }
  if (!store.ready)
    return (
      <>
        <Splash />
        <InstallAndUpdate />
      </>
    );
  if (
    pendingInvite ||
    inviteLink.kind !== "none" ||
    !store.profile ||
    !store.identity ||
    !store.gym
  )
    return (
      <>
        <Onboarding inviteLink={inviteLink} onInviteLinkDone={dismissInviteLink} />
        <InstallAndUpdate />
      </>
    );

  /**
   * Il rifiuto dell'invito prende tutta la schermata, e viene prima di ogni
   * altra cosa. Non è un avviso in cima a un'app che continua a funzionare —
   * come quello della revoca, dove il calendario resta buono da consultare:
   * qui la palestra non ha mai ricevuto niente, e ogni cosa che si potrebbe
   * toccare sotto sarebbe finta. Prima di questa schermata l'unico segnale era
   * «in attesa del coach», per sempre.
   */
  if (store.profile.rejectedAccess) return <RejectedInviteScreen />;

  const isCoach = store.profile.role !== "member";
  const ownReceipt = (broadcastId: string) =>
    store.broadcastReceipts.find(
      (receipt) => receipt.broadcastId === broadcastId && receipt.memberId === store.identity!.id,
    );
  const unreadBroadcasts = isCoach
    ? 0
    : store.broadcasts.filter(({ message }) => !ownReceipt(message.id)?.readAt).length;

  return (
    // La cornice è una sola per ogni ruolo e ogni scheda: quando la larghezza
    // dipendeva dalla pagina, cambiare tab da browser faceva saltare tutto il
    // layout. La larghezza da schermo grande la decide il CSS, non il ruolo.
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Vai al contenuto
      </a>
      <header className="topbar">
        <div>
          <p className="eyebrow">{store.gym.name}</p>
          <h1>Ciao, {store.profile.displayName}</h1>
        </div>
        <SyncBadge />
      </header>

      <SyncNotice />
      <RevokedAccessNotice />
      <StaffSilenceNotice />
      {store.error ? (
        <div className="notice notice-error" role="alert">
          <span>{store.error}</span>
          <button className="text-button" onClick={store.clearError}>
            Chiudi
          </button>
        </div>
      ) : null}
      {!store.backupConfirmed ? (
        <button className="backup-reminder" onClick={() => setPage("settings")}>
          <span aria-hidden="true">◌</span>
          Salva le 12 parole per non perdere il tuo profilo
        </button>
      ) : null}

      <main id="main-content">
        {/* Un solo confine di sospensione per le pagine: i chunk per ruolo si
            risolvono qui, e il resto della cornice — testata, avvisi, nav —
            resta al suo posto mentre arrivano. */}
        <Suspense
          fallback={
            <p className="hint" role="status">
              Caricamento…
            </p>
          }
        >
          {page === "home" && (isCoach ? <CoachHome compact /> : <MemberHome />)}
          {page === "planning" && <Planning />}
          {page === "coach" && isCoach && <CoachHome />}
          {page === "broadcast" && <BroadcastPage />}
          {page === "settings" && <Settings />}
        </Suspense>
      </main>

      <nav className="bottom-nav" aria-label="Navigazione principale">
        <NavButton active={page === "home"} onClick={() => setPage("home")} icon="⌂">
          Oggi
        </NavButton>
        {isCoach ? (
          <NavButton active={page === "coach"} onClick={() => setPage("coach")} icon="◉">
            Coach
          </NavButton>
        ) : null}
        <NavButton active={page === "broadcast"} onClick={() => setPage("broadcast")} icon="✦">
          {isCoach ? "Broadcast" : unreadBroadcasts > 0 ? `Avvisi (${unreadBroadcasts})` : "Avvisi"}
        </NavButton>
        <NavButton active={page === "planning"} onClick={() => setPage("planning")} icon="▦">
          Settimana
        </NavButton>
        <NavButton active={page === "settings"} onClick={() => setPage("settings")} icon="⚙">
          Profilo
        </NavButton>
      </nav>
      <InstallAndUpdate />
    </div>
  );
}

/**
 * Il cartello di chi ha usato un invito che non valeva più. Il caso vero è il
 * link inoltrato: due persone ricevono lo stesso, la prima entra, la seconda
 * apre e — finché non c'è stata questa schermata — entrava anche lei, vedeva
 * il calendario, prenotava, e restava «in attesa del coach» a tempo
 * indeterminato mentre la palestra scartava in silenzio ogni suo messaggio.
 */
function RejectedInviteScreen() {
  const store = useAppStore();
  const rejected = store.profile!.rejectedAccess!;
  const [wiping, setWiping] = useState(false);
  return (
    <>
      <CenteredCard title="Questo invito non è più valido">
        <p className="lead">
          {rejected.reason === "spent"
            ? "Il link che hai aperto era già stato usato da un’altra persona: un invito fa entrare una persona sola, e la palestra non ha registrato il tuo ingresso."
            : "L’invito che hai usato era stato ritirato prima che tu lo aprissi, e la palestra non ha registrato il tuo ingresso."}
        </p>
        <p>
          Quello che vedi su questo telefono non è arrivato a nessuno: le prenotazioni fatte da qui
          non risultano in palestra. Chiedi al coach un invito tuo — solo per te — e aprilo su
          questo dispositivo dopo averlo azzerato.
        </p>
        <button className="primary" onClick={() => setWiping(true)}>
          Azzera questo dispositivo
        </button>
        <p className="hint">
          Segnalato il {shortInstant(rejected.rejectedAt, true)}. L’azzeramento cancella il profilo
          che non è mai entrato: serve per ripartire pulito con l’invito nuovo.
        </p>
      </CenteredCard>
      {wiping ? <WipeDeviceDialog onClose={() => setWiping(false)} /> : null}
      <InstallAndUpdate />
    </>
  );
}

/**
 * Il cartello di chi e' stato revocato. L'envelope `role.revoked` arrivava già
 * su questo dispositivo, ma nessuno diceva all'atleta che il soggetto era lui:
 * continuava a prenotare con successo apparente mentre i pari scartavano tutto.
 * Il calendario resta consultabile — e' quello che serve per orientarsi — ma
 * questa riga resta finche' un invito nuovo non riapre l'accesso.
 */
function RevokedAccessNotice() {
  const store = useAppStore();
  const revoked = store.profile?.revokedAccess;
  if (!revoked) return null;
  return (
    <div className="notice notice-error" role="alert">
      <span>
        Il coach ha revocato il tuo accesso a {store.gym?.name ?? "questa palestra"} il{" "}
        {shortInstant(revoked.revokedAt)}. Puoi ancora consultare il calendario, ma ciò che tocchi o
        selezioni non viene più ricevuto dalla palestra. Per rientrare chiedi al coach un nuovo
        invito e apri il link su questo dispositivo.
      </span>
    </div>
  );
}

/**
 * Oltre questo silenzio dello staff scatta l'avviso. Sette giorni: il
 * riallineamento parte a ogni canale autenticato e un coach attivo risponde
 * con lo stato entro poche ore, quindi una settimana muta non e' una palestra
 * tranquilla — e' una revoca mai recapitata, o un coach sparito. In entrambi i
 * casi le prenotazioni potrebbero parlare al vuoto, e va detto.
 */
const STAFF_SILENCE_WARNING_MS = 7 * 24 * 60 * 60 * 1000;

function StaffSilenceNotice() {
  const store = useAppStore();
  const profile = store.profile;
  const lastContact = store.lastStaffContactAt;
  // Solo per l'atleta: lo staff ha le pianificazioni vere e non dipende da
  // nessuno. Il revocato ha già il suo cartello, più preciso di questo.
  if (!profile || profile.role !== "member" || profile.revokedAccess || !lastContact) return null;
  const silentForMs = Date.now() - Date.parse(lastContact);
  if (silentForMs < STAFF_SILENCE_WARNING_MS) return null;
  const days = Math.floor(silentForMs / (24 * 60 * 60 * 1000));
  return (
    <p className="notice" role="status">
      Nessun aggiornamento dal coach da {days} giorni: le prenotazioni fatte in questo periodo
      potrebbero non essere arrivate in palestra. Chiedi al coach se il tuo accesso è ancora attivo
      — o fatti mandare un nuovo invito.
    </p>
  );
}

function Splash() {
  return (
    <div className="splash">
      <img src="/icon.svg" alt="" />
      <strong>Palestra Insieme</strong>
    </div>
  );
}

type RestoreFallback = "no-backup" | "relay-unreachable" | "unreadable";

/**
 * Le tre cause vanno dette per quello che sono: "non c'è" e "non ho potuto
 * chiedere" portano l'utente a fare cose diverse — la seconda si risolve
 * riprovando piu' tardi, la prima no.
 */
function fallbackExplanation(reason: RestoreFallback): string {
  if (reason === "relay-unreachable") {
    return "Non è stato possibile contattare il relay per cercare la tua copia. L’identità è salva: puoi riprovare più tardi oppure ricollegarti subito con un invito.";
  }
  if (reason === "unreadable") {
    return "La copia trovata sul relay non è leggibile. L’identità è salva: per ritrovare palestra e dati serve un invito.";
  }
  return "Il relay non custodisce una copia del tuo profilo. L’identità è salva: per ritrovare palestra e dati serve un invito.";
}

/**
 * Il testo incollato nel ripiego puo' essere il link corto, il vecchio link
 * lungo con `?invite=` o il solo codice. Il link corto va riscattato dal
 * relay, e i suoi esiti vanno detti con le stesse parole delle altre schermate.
 */
async function inviteFromPastedText(value: string) {
  const trimmed = value.trim();
  if (trimmed) {
    let link;
    try {
      link = parseInviteLinkLocation(new URL(trimmed, location.origin));
    } catch {
      link = undefined;
    }
    if (link) {
      const outcome = await redeemInviteLink(link);
      if (outcome.kind === "invite") return { invite: outcome.invite, token: outcome.token };
      if (outcome.kind === "claimed") throw new Error(LINK_ALREADY_USED);
      if (outcome.kind === "gone") {
        throw new Error("Questo invito è scaduto o è stato ritirato: chiedine uno nuovo");
      }
      if (outcome.kind === "unreachable") {
        throw new Error("Non è stato possibile recuperare l’invito: riprova tra poco");
      }
      throw new Error("Link d’invito non valido");
    }
  }
  return { invite: inviteFromText(value), token: undefined };
}

/**
 * Un invito vale per una persona sola, e questa è la frase che lo dice. Sta
 * qui in una costante perché la stessa cosa va detta con le stesse parole in
 * tutti i punti da cui si può entrare — il link aperto e il testo incollato.
 */
const LINK_ALREADY_USED =
  "Questo link è già stato usato da un’altra persona: chiedi al coach un invito tuo";

function Onboarding({
  inviteLink = { kind: "none" },
  onInviteLinkDone,
}: {
  inviteLink?: InviteLinkView;
  onInviteLinkDone?: () => void;
}) {
  const store = useAppStore();
  const inviteFromQuery = useMemo(inviteFromLocation, []);
  // Il link corto arriva gia' riscattato: da qui in poi i due percorsi — URL
  // lungo con `?invite=` e URL corto `/i/…#…` — sono lo stesso invito firmato.
  const invite = inviteFromQuery ?? (inviteLink.kind === "invite" ? inviteLink.invite : undefined);
  /**
   * Il nome della cassetta sul relay, che c'è solo nel percorso del link corto:
   * serve a chiuderla entrando. Il vecchio `?invite=` non ne ha una, e per lui
   * la difesa resta quella firmata — `spentInvites`, più lenta ma inaggirabile.
   */
  const inviteToken = inviteLink.kind === "invite" ? inviteLink.token : undefined;
  /** Acceso dal relay al momento dell'ingresso: qualcun altro è entrato prima. */
  const [linkTaken, setLinkTaken] = useState(false);
  /** «Sono io»: la persona che ha il telefono in mano dice che il profilo è suo. */
  const [relinking, setRelinking] = useState(false);
  /**
   * Fondare una palestra è irreversibile per chi lo fa per sbaglio: chi apre il
   * link di accesso senza essere il coach non deve nemmeno vedere quel comando.
   * Il contrassegno arriva dal link riservato, che l'edge riconosce.
   */
  const coachEntry = useMemo(() => coachEntryGranted(), []);
  const [mode, setMode] = useState<"welcome" | "founder" | "restore">("welcome");
  const [name, setName] = useState("");
  const [gymName, setGymName] = useState("");
  const [phrase, setPhrase] = useState("");
  const [inviteText, setInviteText] = useState("");
  const [fallback, setFallback] = useState<RestoreFallback | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wiping, setWiping] = useState(false);
  /**
   * Un'identità senza palestra è un dispositivo rimasto a metà: un recupero che
   * non ha trovato la copia, un invito accettato e mai riallineato. È lo stato
   * in cui riprovare non basta — c'è già qualcosa in casa che si mette di mezzo —
   * ed è l'unico in cui questa via d'uscita ha senso mostrarla qui.
   */
  const leftovers = Boolean(store.identity);
  const wipeEntry = leftovers ? (
    <>
      <button className="text-button danger" onClick={() => setWiping(true)}>
        Azzera questo dispositivo
      </button>
      <p className="hint">
        Su questo dispositivo c’è già un profilo. Azzerarlo lascia intatta la copia cifrata sul
        relay e fa ripartire il recupero da pulito.
      </p>
      {wiping ? <WipeDeviceDialog onClose={() => setWiping(false)} /> : null}
    </>
  ) : null;

  const run = async (action: () => Promise<void>) => {
    setError("");
    try {
      await action();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Operazione non riuscita");
    }
  };

  /**
   * Il link era buono, ma qualcun altro c'è arrivato prima. Vale sia quando lo
   * si scopre aprendo il link (`claimed`), sia quando lo si scopre nell'istante
   * dell'ingresso (`linkTaken`): per chi legge è la stessa cosa, e la frase è
   * la stessa.
   */
  if (linkTaken || inviteLink.kind === "claimed") {
    return (
      <CenteredCard title="Link già usato">
        <p className="lead">
          {LINK_ALREADY_USED}. Un invito fa entrare una persona sola: questo l’ha già usato chi l’ha
          ricevuto prima di te.
        </p>
        <button className="secondary" onClick={onInviteLinkDone}>
          Va bene
        </button>
      </CenteredCard>
    );
  }

  if (invite) {
    /**
     * Riaprire il proprio link succede — si torna sulla chat, si ritocca il
     * messaggio del coach — e prima di questo confronto ricominciava l'ingresso
     * da capo: profilo riscritto e pianificazione azzerata, anche in palestra.
     * L'invito è lo stesso: non c'è niente da fare, si è già dentro.
     */
    if (store.profile && store.acceptedInviteId === invite.payload.id) {
      return (
        <CenteredCard title="Sei già dentro">
          <p className="lead">
            Questo invito l’hai già usato tu, su questo telefono: sei in {invite.payload.gymName}{" "}
            come {store.profile.displayName}. Non serve rientrare.
          </p>
          <button className="primary" onClick={onInviteLinkDone}>
            Continua
          </button>
        </CenteredCard>
      );
    }
    /**
     * Un invito diverso su un telefono che ha già un profilo. Due cose molto
     * distanti fra loro hanno questa stessa forma: la persona che si ricollega
     * con un invito nuovo — legittima, e va lasciata passare — e il telefono
     * prestato, dove il nuovo arrivato ereditava l'identità di chi c'era prima
     * — stesse chiavi, stessa persona per la palestra — cancellandone profilo
     * e allenamenti. Nessun segnale tecnico le separa: chi ha il telefono in
     * mano lo sa, l'app no. Quindi si chiede, invece di decidere. Chi è stato
     * revocato non passa nemmeno di qui: rientrare con un invito nuovo dallo
     * stesso telefono è la via d'uscita che gli abbiamo promesso, e va dritta.
     */
    if (store.profile && !store.profile.revokedAccess && !relinking) {
      const owner = store.profile.displayName;
      return (
        <CenteredCard title="Questo telefono è già usato da un’altra persona">
          <p className="lead">
            Su questo telefono c’è il profilo di {owner}, e un invito vale per una persona sola. Se
            il telefono non è tuo, apri il tuo link dal tuo: entrare da qui prenderebbe il posto di{" "}
            {owner}.
          </p>
          <button
            className="primary"
            onClick={() => {
              setName(owner);
              setRelinking(true);
            }}
          >
            Sono {owner}: usa questo invito
          </button>
          <button className="secondary" onClick={onInviteLinkDone}>
            Lascia stare, continua come {owner}
          </button>
          {wipeEntry}
        </CenteredCard>
      );
    }
    return (
      <CenteredCard title={`Invito a ${invite.payload.gymName}`}>
        <p className="lead">
          Entra come {invite.payload.role === "coach" ? "coach" : "atleta"}. Il profilo viene creato
          su questo telefono.
        </p>
        <label>
          Come ti chiami?
          <input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        {error ? (
          <p className="field-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="primary"
          disabled={!name.trim() || busy}
          onClick={() =>
            void run(async () => {
              setBusy(true);
              try {
                /**
                 * Il link si chiude qui, prima che il profilo esista: fra
                 * l'ingresso e il momento in cui il coach registra il consumo
                 * possono passare ore — tutte quelle in cui il coach è offline
                 * — e in quelle ore chiunque riceva il link entrerebbe per poi
                 * scoprirsi, giorni dopo, non essere mai stato in palestra.
                 * Un relay muto non ferma nessuno: la difesa firmata rimane.
                 */
                if (inviteToken && (await claimInviteLink(inviteToken)) === "taken") {
                  setLinkTaken(true);
                  return;
                }
                await store.acceptInvite(name.trim(), invite);
                onInviteLinkDone?.();
              } finally {
                setBusy(false);
              }
            })
          }
        >
          {busy ? "Un attimo…" : "Entra in palestra"}
        </button>
      </CenteredCard>
    );
  }

  if (inviteLink.kind === "loading") {
    return (
      <CenteredCard title="Recupero dell’invito…">
        <p className="lead" role="status">
          Un attimo: stiamo chiedendo l’invito al server.
        </p>
      </CenteredCard>
    );
  }

  // `410 Gone`, non «non trovato»: chi apre un link ritirato o scaduto deve
  // sapere che cosa fare, non concludere che l'applicazione non funziona.
  if (inviteLink.kind === "gone") {
    return (
      <CenteredCard title="Invito scaduto o ritirato">
        <p className="lead">
          Questo invito non è più valido: è scaduto, oppure chi l’ha creato l’ha ritirato. Chiedi un
          invito nuovo e apri il link appena arriva.
        </p>
        <button className="secondary" onClick={onInviteLinkDone}>
          Va bene
        </button>
      </CenteredCard>
    );
  }

  if (inviteLink.kind === "invalid") {
    return (
      <CenteredCard title="Link d’invito non valido">
        <p className="lead">
          Il link è arrivato incompleto: controlla di averlo aperto per intero — anche la parte dopo
          il cancelletto conta — oppure chiedi un invito nuovo.
        </p>
        <button className="secondary" onClick={onInviteLinkDone}>
          Va bene
        </button>
      </CenteredCard>
    );
  }

  if (inviteLink.kind === "unreachable") {
    return (
      <CenteredCard title="Server non raggiungibile">
        <p className="lead">
          Non è stato possibile recuperare l’invito. Il link resta valido: controlla la connessione
          e riprova.
        </p>
        <button className="primary" onClick={() => location.reload()}>
          Riprova
        </button>
        <button className="text-button" onClick={onInviteLinkDone}>
          Più tardi
        </button>
      </CenteredCard>
    );
  }

  if (mode === "founder" && coachEntry) {
    return (
      <CenteredCard title="Crea la palestra">
        <label>
          Il tuo nome
          <input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          Nome della palestra
          <input value={gymName} onChange={(event) => setGymName(event.target.value)} />
        </label>
        {error ? (
          <p className="field-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="primary"
          disabled={!name.trim() || !gymName.trim()}
          onClick={() => void run(() => store.createFounder(name.trim(), gymName.trim()))}
        >
          Crea e configura
        </button>
        <button className="text-button" onClick={() => setMode("welcome")}>
          Indietro
        </button>
      </CenteredCard>
    );
  }

  if (mode === "restore") {
    // Il ripiego compare solo dopo aver saputo che il backup non c'e': chiedere
    // un invito a chi non ne ha bisogno e' la cosa che questo change elimina.
    if (fallback) {
      return (
        <CenteredCard title="Serve un invito">
          <p className="lead">{fallbackExplanation(fallback)}</p>
          <label>
            Il tuo nome
            <input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            Invito della palestra — incolla il link completo oppure solo il codice
            <textarea
              rows={3}
              value={inviteText}
              onChange={(event) => setInviteText(event.target.value)}
            />
          </label>
          {error ? (
            <p className="field-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="primary"
            disabled={!name.trim() || !inviteText.trim() || busy}
            onClick={() =>
              void run(async () => {
                setBusy(true);
                try {
                  const pasted = await inviteFromPastedText(inviteText);
                  // Anche qui il link si chiude entrando, per la stessa ragione.
                  if (pasted.token && (await claimInviteLink(pasted.token)) === "taken") {
                    throw new Error(LINK_ALREADY_USED);
                  }
                  await store.acceptInvite(name.trim(), pasted.invite);
                } finally {
                  setBusy(false);
                }
              })
            }
          >
            Ricollega alla palestra
          </button>
          <button
            className="text-button"
            onClick={() => {
              setFallback(undefined);
              setMode("welcome");
            }}
          >
            Più tardi
          </button>
        </CenteredCard>
      );
    }
    return (
      <CenteredCard title="Recupera il profilo">
        <p className="lead">
          Bastano le 12 parole: palestra, ruolo e pianificazione tornano da soli, se il relay ne
          custodisce una copia.
        </p>
        <label>
          Le tue 12 parole
          <textarea
            rows={5}
            value={phrase}
            onChange={(event) => setPhrase(event.target.value)}
            autoFocus
          />
        </label>
        {error ? (
          <p className="field-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="primary"
          disabled={!phrase.trim() || busy}
          onClick={() =>
            void run(async () => {
              setBusy(true);
              try {
                const outcome = await store.restoreProfile(phrase);
                if (outcome.kind === "identity-only") setFallback(outcome.reason);
              } finally {
                setBusy(false);
              }
            })
          }
        >
          {busy ? "Recupero in corso…" : "Recupera il profilo"}
        </button>
        <button className="text-button" onClick={() => setMode("welcome")}>
          Indietro
        </button>
        {wipeEntry}
      </CenteredCard>
    );
  }

  return (
    <div className="onboarding">
      <img className="onboarding-logo" src="/icon.svg" alt="" />
      <p className="eyebrow">Presenze senza complicazioni</p>
      <h1>La palestra, in tasca.</h1>
      <p className="lead">
        Il coach crea la palestra e invita gli atleti. Tutti vedono gli stessi orari, anche offline.
      </p>
      <div className="onboarding-actions">
        {coachEntry ? (
          <button className="primary" onClick={() => setMode("founder")}>
            Crea una palestra come coach
          </button>
        ) : null}
        <button className="text-button" onClick={() => setMode("restore")}>
          Ho già le 12 parole
        </button>
      </div>
      <p className="hint onboarding-invite-hint">
        {coachEntry
          ? "Hai aperto il link riservato al coach: da qui nasce la palestra. Gli atleti entreranno dall’invito che manderai loro."
          : "Sei un atleta? Apri su questo dispositivo il link ricevuto dal coach. La palestra la crea il coach, da un link suo."}
      </p>
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      {wipeEntry}
    </div>
  );
}

function MemberHome() {
  const store = useAppStore();
  const gym = store.gym;
  const schedule = store.schedules.find((item) => item.memberId === store.identity?.id);
  /**
   * L'istante di partenza e' arrotondato al minuto: un `toISOString()` nudo
   * cambia a ogni render e renderebbe inutile la memo qui sotto (PERF-06).
   */
  const nowMinute = `${new Date().toISOString().slice(0, 16)}:00Z`;
  const occurrences = useMemo(
    () => (gym && schedule ? occurrencesBetween(gym, schedule, nowMinute, 35) : []),
    [gym, schedule, nowMinute],
  );
  const next = occurrences[0];
  const [changing, setChanging] = useState(false);
  if (!schedule || !store.gym)
    return (
      <EmptyState
        title="Scegli la tua settimana"
        text="Indica gli orari in cui ti alleni di solito."
      />
    );

  return (
    <section className="stack member-home">
      <div className="hero-card">
        <p className="eyebrow">Prossima lezione</p>
        {next ? (
          <>
            <h2>{formatDay(next.startsAt)}</h2>
            <p className="hero-time">{formatTime(next.startsAt)}</p>
            {/* La capienza serve prima del cambio, non dopo: e' la ragione per
                cui si apre questa schermata. Qui resta senza tonalita': il
                blocco e' gia' fluo pieno, e la scala della capienza su quel
                fondo non raggiungerebbe il contrasto. Restano numeri e parola. */}
            <p className="hero-fill">{occupancyDetail(readOccupancy(store.occupancy, next.key))}</p>
            <button className="primary light" onClick={() => setChanging(true)}>
              Cambia slot
            </button>
          </>
        ) : (
          <EmptyState
            title="Nessuna lezione in programma"
            text="Apri Settimana e scegli i tuoi slot abituali."
          />
        )}
      </div>
      <section>
        <div className="section-heading">
          <h2>Le prossime</h2>
        </div>
        <div className="list-card">
          {occurrences.slice(0, 5).map((entry) => {
            const reading = readOccupancy(store.occupancy, entry.key);
            return (
              <div className="lesson-row" key={entry.key}>
                <div className="date-tile">
                  <strong>{new Date(entry.startsAt).getDate()}</strong>
                  <span>{formatWeekday(entry.startsAt)}</span>
                </div>
                <div>
                  <strong>{formatTime(entry.startsAt)}</strong>
                  <small>
                    {entry.status === "conflicted" ? "Da verificare" : "Confermata localmente"}
                    {reading ? " · " : ""}
                    {reading ? (
                      <span className={fillClass(reading.level).trim()}>
                        {reading.count}/{reading.capacity} {occupancyLabels[reading.level]}
                      </span>
                    ) : null}
                  </small>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      {changing && next ? (
        <ScheduleActionDialog occurrence={next} onClose={() => setChanging(false)} />
      ) : null}
    </section>
  );
}

type ScheduleIntent = RecurringIntent;

/**
 * Il glifo non e' la sola informazione: accompagna sempre la tonalita' della
 * scala e l'etichetta testuale, perche' la capienza deve restare leggibile
 * anche a chi non distingue i colori o usa uno screen reader.
 */
const occupancyGlyphs: Record<OccupancyLevel, string> = {
  free: "\u00b7",
  filling: "\u25aa",
  near: "\u25b2",
  full: "\u2297",
  over: "!",
};

/**
 * Il livello da solo non basta a decidere: "quasi pieno" e' 8 su 10 o 1 su 1?
 * I numeri stanno accanto alla parola, i posti residui la traducono in italiano.
 */
function occupancyDetail(reading: OccupancyReading | undefined): string {
  if (!reading) return "Capienza non ancora comunicata dal coach";
  const remaining = reading.capacity - reading.count;
  const head = `${occupancyLabels[reading.level]} \u00b7 ${reading.count}/${reading.capacity}`;
  if (remaining <= 0) return `${head}, nessun posto libero`;
  return `${head}, ${remaining} ${remaining === 1 ? "posto libero" : "posti liberi"}`;
}

function ScheduleActionDialog({
  occurrence,
  availableSlot,
  date: availableDate,
  full = false,
  onClose,
}: {
  occurrence?: Occurrence;
  availableSlot?: Gym["slots"][number];
  date?: string;
  full?: boolean;
  onClose: () => void;
}) {
  const store = useAppStore();
  const gym = store.gym;
  const schedule = store.schedules.find((item) => item.memberId === store.identity?.id);
  const selectedDate = occurrence?.date ?? availableDate;
  /**
   * Cosa comporta davvero toccare quello slot, letto dal vincolo di un solo slot
   * al giorno: da qui vengono sia il titolo sia la domanda sulla durata della
   * modifica. La schermata non decide, riferisce.
   */
  const plan =
    gym && schedule && selectedDate && availableSlot
      ? planDayBooking(gym, schedule, selectedDate, availableSlot.id)
      : undefined;
  const [intent, setIntent] = useState<ScheduleIntent | undefined>(() =>
    availableSlot && (plan?.kind === "book" || plan?.kind === "move")
      ? { kind: plan.kind, targetSlotId: availableSlot.id }
      : undefined,
  );
  const [saving, setSaving] = useState(false);
  /**
   * A un revocato il tocco sulla cella non apre più prenotazioni: aprirebbe
   * un percorso che finisce nel vuoto. Al suo posto, la spiegazione — la
   * stessa del cartello in alto, nel punto esatto in cui l'atleta sta
   * provando a fare la cosa che non ha più effetto.
   */
  if (store.profile?.revokedAccess) {
    return (
      <Modal onClose={onClose} labelledBy="schedule-action-title">
        <p className="eyebrow">Accesso alla palestra</p>
        <h2 id="schedule-action-title">Accesso revocato</h2>
        <p>
          Il coach ha revocato il tuo accesso: puoi consultare il calendario, ma selezionare o
          prenotare non ha più effetto — la palestra non riceve i tuoi messaggi.
        </p>
        <p>Per rientrare chiedi al coach un nuovo invito e apri il link su questo dispositivo.</p>
        <button className="text-button" onClick={onClose}>
          Chiudi
        </button>
      </Modal>
    );
  }
  if (!gym || !schedule || !selectedDate) return null;
  const offered = availableSlotsForDate(gym, selectedDate);
  /**
   * L'impegno da cui si parte: quello aperto dalla schermata, oppure quello che
   * la giornata ha gia' e che la prenotazione nuova va a prendere il posto di.
   */
  const source =
    occurrence ??
    (plan?.kind === "move" ? plan.from : plan?.kind === "kept" ? plan.occurrence : undefined);
  const dayOccurrences = occurrencesForDate(gym, schedule, selectedDate);
  const occupied = new Set(dayOccurrences.map((entry) => entry.slotId));
  const alternatives = offered.filter((slot) => !occupied.has(slot.id));
  const targetSlot =
    intent?.kind === "cancel"
      ? undefined
      : (offered.find((slot) => slot.id === intent?.targetSlotId) ?? availableSlot);
  const targetReading = targetSlot
    ? readOccupancy(store.occupancy, `${selectedDate}:${targetSlot.id}`)
    : undefined;
  /**
   * Pieno e' una proprieta' del bersaglio, non della cella da cui si e' partiti:
   * la prop `full` vale solo finche' il bersaglio e' lo slot toccato, altrimenti
   * uno slot sforato bloccherebbe anche lo spostamento verso un orario libero.
   */
  const targetIsFull = targetReading
    ? targetReading.level === "full" || targetReading.level === "over"
    : full && targetSlot?.id === availableSlot?.id;
  /** Uno slot pieno non si prenota dall'interfaccia: la schermata informa, non conferma. */
  const bookingBlocked = intent?.kind !== "cancel" && targetIsFull;
  const hasRecurringSource = Boolean(
    source && schedule.recurringSlotIds.includes(source.sourceSlotId),
  );
  /** Lo spostamento nato da un tocco su un altro orario, non dal comando «Sposta». */
  const replacesDay = plan?.kind === "move";

  const commit = async (scope: "single" | "recurring") => {
    if (!intent) return;
    setSaving(true);
    try {
      if (scope === "single") {
        if (intent.kind === "cancel") {
          if (source) await store.cancel(selectedDate, source.sourceSlotId);
        } else {
          // Prenotare e spostare sono la stessa richiesta — «esserci a quell'ora»
          // — e quale delle due sia lo dice il vincolo, non questa schermata.
          await store.book(selectedDate, intent.targetSlotId);
        }
      } else {
        await store.updateSchedule(
          applyRecurringChange(gym, schedule, selectedDate, source, intent),
        );
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} labelledBy="schedule-action-title">
      <p className="eyebrow">{formatDateOnly(selectedDate)}</p>
      {plan?.kind === "spent" ? (
        <>
          <h2 id="schedule-action-title">Giornata già usata</h2>
          {/* Spostare un impegno trascorso vorrebbe dire cancellare un
              allenamento fatto: qui non c'e' una scelta da offrire, c'e' una
              ragione da dare. */}
          <p>
            Eri dentro alle {formatTime(plan.from.startsAt)}. In una giornata c’è posto per un solo
            slot, e quello di questa data è già passato.
          </p>
        </>
      ) : !intent ? (
        <>
          <h2 id="schedule-action-title">La tua presenza</h2>
          <p>{source ? `Sei dentro alle ${formatTime(source.startsAt)}.` : null}</p>
          <div className="slot-options">
            <button onClick={() => setIntent({ kind: "move", targetSlotId: "" })}>
              <strong>Sposta</strong>
              <span>Un altro orario dello stesso giorno</span>
            </button>
            <button onClick={() => setIntent({ kind: "cancel" })}>
              <strong>Annulla presenza</strong>
              <span>Scegli se solo questa volta o ricorrente</span>
            </button>
          </div>
        </>
      ) : intent.kind === "move" && !intent.targetSlotId ? (
        <>
          <h2 id="schedule-action-title">Scegli il nuovo orario</h2>
          {alternatives.length > 0 ? (
            <div className="slot-options">
              {alternatives.map((slot) => {
                const reading = readOccupancy(store.occupancy, `${selectedDate}:${slot.id}`);
                const slotFull = reading?.level === "full" || reading?.level === "over";
                return (
                  <button
                    key={slot.id}
                    disabled={slotFull}
                    onClick={() => setIntent({ kind: "move", targetSlotId: slot.id })}
                  >
                    <strong>{slot.startTime}</strong>
                    <span className={fillClass(reading?.level).trim()}>
                      {occupancyDetail(reading)}
                      {slotFull ? ": non prenotabile" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="hint">Non ci sono altri orari disponibili in questo giorno.</p>
          )}
        </>
      ) : (
        <>
          <h2 id="schedule-action-title">
            {intent.kind === "cancel"
              ? "Annulla la presenza"
              : intent.kind === "move"
                ? `Sposta alle ${targetSlot?.startTime}`
                : `Prenota alle ${targetSlot?.startTime}`}
          </h2>
          {intent.kind !== "cancel" && targetReading ? (
            <p
              className={targetIsFull ? "capacity-warning" : "hint"}
              role={targetIsFull ? "status" : undefined}
            >
              {targetIsFull
                ? `${occupancyDetail(targetReading)}. Questo slot è pieno: non è prenotabile.`
                : occupancyDetail(targetReading)}
            </p>
          ) : null}
          {intent.kind !== "cancel" && !targetReading && targetIsFull ? (
            <p className="capacity-warning" role="status">
              Questo slot risulta pieno secondo i dati locali: non è prenotabile.
            </p>
          ) : null}
          {/* Un tocco su un altro orario non aggiunge un secondo allenamento:
              prende il posto di quello che c'era. Dirlo prima di confermare e'
              l'unica difesa contro il «pensavo di averne prenotati due». */}
          {!bookingBlocked && replacesDay && source ? (
            <p className="hint">
              Un solo slot al giorno: confermando lasci le {formatTime(source.startsAt)}.
            </p>
          ) : null}
          {/* Bloccato vuol dire senza conferme: la schermata spiega e basta,
              l'unica uscita e' «Chiudi». */}
          {bookingBlocked ? null : (
            <>
              {intent.kind === "cancel" && !hasRecurringSource ? (
                <p className="hint">
                  Questa è una presenza singola e verrà rimossa solo da questa data.
                </p>
              ) : intent.kind === "move" && hasRecurringSource && source ? (
                <p>
                  Le {formatTime(source.startsAt)} sono il tuo orario fisso del{" "}
                  {weekdayName(isoWeekday(selectedDate)).toLocaleLowerCase("it")}: lo spostamento
                  vale solo per questa data o d’ora in poi?
                </p>
              ) : (
                <p>Scegli quanto deve durare questa modifica.</p>
              )}
              <div className="scope-options">
                <button className="primary" disabled={saving} onClick={() => void commit("single")}>
                  Solo questa volta
                </button>
                {intent.kind !== "cancel" || hasRecurringSource ? (
                  <button
                    className="secondary"
                    disabled={saving}
                    onClick={() => void commit("recurring")}
                  >
                    D’ora in poi, ogni{" "}
                    {weekdayName(isoWeekday(selectedDate)).toLocaleLowerCase("it")}
                  </button>
                ) : null}
              </div>
            </>
          )}
        </>
      )}
      <button className="text-button" onClick={onClose}>
        Chiudi
      </button>
    </Modal>
  );
}

function Planning() {
  const store = useAppStore();
  const schedule = store.schedules.find((item) => item.memberId === store.identity?.id);
  if (!store.gym) return null;
  if (store.profile?.role !== "member") return <GymEditor />;
  if (!schedule)
    return (
      <EmptyState
        title="Pianificazione non disponibile"
        text="Riapri l’invito o attendi la sincronizzazione."
      />
    );
  if (store.awaitingInitialSync && store.gym.slots.length === 0) {
    return (
      <EmptyState
        title="In attesa del coach"
        text="La richiesta è salvata. Gli slot compariranno quando un coach si collegherà al relay."
      />
    );
  }
  return <MemberWeekGrid gym={store.gym} schedule={schedule} />;
}

function MemberWeekGrid({ gym, schedule }: { gym: Gym; schedule: MemberSchedule }) {
  const store = useAppStore();
  const today = dateInTimeZone(new Date(), gym.timeZone);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selected, setSelected] = useState<{
    date: string;
    occurrence?: Occurrence;
    slot?: Gym["slots"][number];
    full: boolean;
  }>();
  const currentWeekStart = startOfIsoWeek(today);
  const weekStart = addPlainDateDays(currentWeekStart, weekOffset * 7);
  /**
   * L'intera lettura della settimana dipende solo da palestra, pianificazione e
   * settimana scelta: rifarla a ogni render faceva pagare il costo di
   * `occurrencesBetween` per ogni battito del badge di sync (PERF-06).
   */
  const { dates, occurrenceByCell, holdByDate, offeredByDate, times } = useMemo(() => {
    const dates = Array.from({ length: 7 }, (_, index) => addPlainDateDays(weekStart, index));
    const dateSet = new Set(dates);
    const from = instantAtZonedDateTime(weekStart, "00:00", gym.timeZone);
    const occurrences = occurrencesBetween(gym, schedule, from, 6).filter((entry) =>
      dateSet.has(entry.date),
    );
    const occurrenceByCell = new Map(
      occurrences.map((entry) => [`${entry.date}:${entry.slotId}`, entry]),
    );
    /**
     * Chi tiene occupata ogni giornata: e' la stessa lettura che governa la
     * scrittura, cosi' la cella promette esattamente quello che poi succede.
     */
    const holdByDate = new Map(
      dates.map((date) => [date, dayHold(occurrences.filter((entry) => entry.date === date))]),
    );
    const offeredByDate = new Map(dates.map((date) => [date, availableSlotsForDate(gym, date)]));
    const times = [
      ...new Set(
        dates.flatMap((date) => (offeredByDate.get(date) ?? []).map((slot) => slot.startTime)),
      ),
    ].sort();
    return { dates, occurrenceByCell, holdByDate, offeredByDate, times };
  }, [gym, schedule, weekStart]);

  // Il titolo è la settimana che si sta guardando, non la parola "Settimana":
  // il nome della vista lo dice già la barra in basso.
  const weekLabel = `${capitalizeWords(formatDateOnly(weekStart))} – ${capitalizeWords(
    formatDateOnly(dates[6]!),
  )}`;
  return (
    <section className="stack">
      <PageTitle
        eyebrow="La tua pianificazione"
        title={weekLabel}
        text="Tocca uno slot per prenotare, spostare o annullare una data reale. In una giornata c’è posto per un solo slot: toccarne un altro sposta la presenza."
      />
      <div className="week-navigation">
        <button
          className="week-arrow"
          aria-label="Settimana precedente"
          onClick={() => setWeekOffset((value) => value - 1)}
        >
          ‹
        </button>
        <div className="week-navigation-current">
          {/* Il periodo visibile è il titolo della pagina: qui resta l'annuncio
              per gli screen reader (e per i test), che il titolo non fa. */}
          <strong className="sr-only" aria-live="polite">
            {formatDateOnly(weekStart)} – {formatDateOnly(dates[6]!)}
          </strong>
          {/* Il ritorno resta visibile anche quando non ha nulla da fare: un
              comando che compare e scompare va cercato ogni volta. L'etichetta
              estesa lo distingue dalla voce "Oggi" della barra in basso. */}
          <button
            className="secondary week-today"
            aria-label="Vai alla settimana di oggi"
            disabled={weekOffset === 0}
            onClick={() => setWeekOffset(0)}
          >
            Oggi
          </button>
        </div>
        <button
          className="week-arrow"
          aria-label="Settimana successiva"
          onClick={() => setWeekOffset((value) => value + 1)}
        >
          ›
        </button>
      </div>
      {store.occupancy ? null : (
        <p className="hint">
          La capienza compare quando un coach si collega: fino ad allora vedi soltanto i tuoi
          impegni.
        </p>
      )}
      <div className="week-grid-scroll">
        <table className="week-grid">
          <caption className="sr-only">Presenze e slot della settimana selezionata</caption>
          <colgroup>
            <col className="week-time-column" />
            <col span={7} />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Ora</th>
              {dates.map((date) => {
                const day = readDayOccupancy(store.occupancy, date);
                // Le date sono ISO: il confronto lessicografico e' cronologico.
                const isToday = date === today;
                return (
                  <th
                    scope="col"
                    key={date}
                    className={dayClassName(date, today)}
                    aria-current={isToday ? "date" : undefined}
                  >
                    {/* "oggi" prende il posto dell'abbreviazione del giorno: il
                        numero sotto dice comunque la data, e cosi' la colonna
                        corrente si riconosce senza il bianco della selezione. */}
                    <span>{isToday ? "oggi" : shortWeekday(date)}</span>
                    <strong>{plainDateDay(date)}</strong>
                    {/* Questo riassunto e' la GIORNATA intera, non l'ora: somma
                        tutti gli slot della data. La cella sotto parla del
                        proprio slot, e finora le due misure si dicevano con la
                        stessa etichetta. I numeri e il nome esteso lo
                        dichiarano. */}
                    {day ? (
                      <small className={`day-fill fill-${day.level}`}>
                        <b aria-hidden="true">
                          {day.count}/{day.capacity}
                        </b>
                        <span aria-hidden="true">{occupancyLabels[day.level]}</span>
                        <span className="sr-only">
                          Giornata: {occupancyLabels[day.level].toLocaleLowerCase("it")},{" "}
                          {day.count} posti occupati su {day.capacity} in tutti gli slot
                        </span>
                      </small>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {times.map((time) => (
              <tr key={time}>
                <th scope="row">{time}</th>
                {dates.map((date) => {
                  const slots = (offeredByDate.get(date) ?? []).filter(
                    (slot) => slot.startTime === time,
                  );
                  const slot =
                    slots.find((candidate) => occurrenceByCell.has(`${date}:${candidate.id}`)) ??
                    slots[0];
                  if (!slot)
                    // Senza slot non c'e' un'ora da confrontare: la cella vuota
                    // segue la giornata, cosi' la colonna trascorsa si legge
                    // intera invece di risultare bucata.
                    return (
                      <td
                        className={`week-cell week-cell-empty ${dayClassName(date, today)}`.trim()}
                        key={date}
                      >
                        <span className="sr-only">Nessuno slot</span>
                      </td>
                    );
                  const occurrence = occurrenceByCell.get(`${date}:${slot.id}`);
                  const reading = readOccupancy(store.occupancy, `${date}:${slot.id}`);
                  const isFull = reading?.level === "full" || reading?.level === "over";
                  const isPast =
                    new Date(instantAtZonedDateTime(date, slot.startTime, gym.timeZone)).getTime() <
                    Date.now();
                  // Cosa succede toccando una cella libera dipende dalla giornata,
                  // non dall'orario: se la giornata e' gia' impegnata, questo tocco
                  // e' uno spostamento, e la cella lo dice prima di essere premuta.
                  const hold = holdByDate.get(date);
                  const replaces = occurrence ? undefined : hold?.from;
                  // Lo stato proprio viene prima della capienza: "sei dentro" e' la
                  // risposta alla domanda che l'atleta si fa guardando la griglia.
                  const state = occurrence ? "own" : (reading?.level ?? "unknown");
                  const stateLabel = occurrence
                    ? "Sei dentro"
                    : reading
                      ? occupancyLabels[reading.level]
                      : "Disponibile";
                  return (
                    <td
                      className={`week-cell week-cell-${state} ${
                        isPast ? "is-past" : dayClassName(date, today)
                      }`.trim()}
                      key={date}
                    >
                      <button
                        disabled={isPast}
                        aria-label={`${stateLabel}: ${formatDateOnly(date)} alle ${time}. ${
                          reading ? `${occupancyDetail(reading)}. ` : ""
                        }${
                          isPast
                            ? "Data passata: non è più modificabile"
                            : occurrence
                              ? "Apri le azioni della presenza"
                              : replaces && hold?.spent
                                ? `Giornata già usata: eri dentro alle ${formatTime(replaces.startsAt)}`
                                : isFull
                                  ? "Slot pieno: non prenotabile. Apri i dettagli"
                                  : replaces
                                    ? `Sposta qui la presenza delle ${formatTime(replaces.startsAt)}`
                                    : "Prenota lo slot"
                        }`}
                        onClick={() =>
                          setSelected(
                            occurrence
                              ? { date, occurrence, full: Boolean(isFull) }
                              : { date, slot, full: Boolean(isFull) },
                          )
                        }
                      >
                        <span aria-hidden="true">
                          {occurrence ? "━" : reading ? occupancyGlyphs[reading.level] : "·"}
                        </span>
                        {/* I numeri dello slot stanno nella cella: il glifo dice
                            il livello, ma "2/10" e "2/1" sono decisioni diverse.
                            Nel blocco della propria presenza lo sforamento non
                            puo' restare verde: la pastiglia rossa lo dichiara. */}
                        {reading ? (
                          <small
                            aria-hidden="true"
                            className={`cell-count${
                              occurrence && reading.level === "over" ? " cell-count-over" : ""
                            }`}
                          >
                            {reading.count}/{reading.capacity}
                          </small>
                        ) : null}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hint">
        Sotto ogni giorno c’è quanto è piena l’<strong>intera giornata</strong>; dentro la griglia,
        quanto è pieno quel singolo orario.
      </p>
      <ul className="week-legend" aria-label="Legenda stati">
        <li>
          <span aria-hidden="true">□</span>Nessuno slot
        </li>
        <li>
          <span aria-hidden="true">━</span>Sei dentro
        </li>
        <li className="fill-free">
          <span aria-hidden="true">·</span>Libero
        </li>
        <li className="fill-filling">
          <span aria-hidden="true">▪</span>Si sta riempiendo
        </li>
        <li className="fill-near">
          <span aria-hidden="true">▲</span>Quasi pieno
        </li>
        <li className="fill-full">
          <span aria-hidden="true">⊗</span>Pieno
        </li>
        <li className="fill-over">
          <span aria-hidden="true">!</span>Oltre soglia
        </li>
        <li className="week-legend-past">
          <span aria-hidden="true">▨</span>Passata, non modificabile
        </li>
      </ul>
      {selected?.occurrence ? (
        <ScheduleActionDialog
          key={`occurrence:${selected.occurrence.key}`}
          occurrence={selected.occurrence}
          full={selected.full}
          onClose={() => setSelected(undefined)}
        />
      ) : selected?.slot ? (
        <ScheduleActionDialog
          key={`slot:${selected.date}:${selected.slot.id}`}
          availableSlot={selected.slot}
          date={selected.date}
          full={selected.full}
          onClose={() => setSelected(undefined)}
        />
      ) : null}
    </section>
  );
}

/** Quante parole chiediamo di ricopiare per considerare annotata la frase. */
const BACKUP_CHALLENGE_SIZE = 3;

/**
 * Tre stati e non un booleano: la differenza fra "sto guardando le parole" e
 * "sto verificando senza vederle" è esattamente ciò che rende la conferma una
 * verifica. Tenerla nel tipo rende impossibile per costruzione lo stato in cui
 * la frase resta a schermo mentre la si ricopia.
 */
type BackupStep = "hidden" | "revealed" | "verifying";

type BackupChallenge = {
  positions: number[];
  answers: string[];
  /** Esito dell'ultima verifica, non uno stato permanente del campo. */
  errors: boolean[];
};

/**
 * L'esito viene detto per intero, ripiego compreso: un "copiato" scritto anche
 * quando la copia non è avvenuta manderebbe la persona a incollare il nulla.
 */
function linkNote(outcome: CopyOutcome): string {
  return outcome === "copied"
    ? "Link copiato: incollalo dove vuoi, anche su WhatsApp."
    : "Il browser non ha concesso gli appunti: seleziona il link qui sotto e copialo a mano.";
}

function qrNote(outcome: CopyOutcome): string {
  if (outcome === "copied") return "QR copiato come immagine: incollalo nella chat.";
  if (outcome === "downloaded")
    return "Il browser non copia le immagini: il QR è stato salvato tra i download, allegalo dalla galleria.";
  return "QR non copiato: resta il link qui sotto.";
}

function Settings() {
  const store = useAppStore();
  const [backupStep, setBackupStep] = useState<BackupStep>("hidden");
  const [challenge, setChallenge] = useState<BackupChallenge>();
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [diagnostics, setDiagnostics] = useState<{
    persisted: boolean;
    usage: number;
    quota: number;
  }>();
  const [removing, setRemoving] = useState(false);
  const [wiping, setWiping] = useState(false);
  const phrase = store.identity ? identityToMnemonic(store.identity) : "";
  const words = phrase.split(" ");
  // Estratte qui e non durante il render: calcolarle nel corpo del componente
  // le cambierebbe a ogni battuta sui tasti, rendendo la verifica incompletabile.
  const startVerification = () => {
    const positions = pickChallengeIndexes(words.length, BACKUP_CHALLENGE_SIZE);
    setChallenge({
      positions,
      answers: positions.map(() => ""),
      errors: positions.map(() => false),
    });
    setBackupStep("verifying");
  };
  /**
   * La via d'uscita resta sempre aperta, ma passare di qui scarta la sfida in
   * corso. Al rientro le posizioni sono altre: così rileggere serve a rifare il
   * backup per intero, non a recuperare la singola parola che mancava.
   */
  const reviewPhrase = () => {
    setChallenge(undefined);
    setBackupStep("revealed");
  };
  const answerWord = (index: number, value: string) => {
    setChallenge((previous) =>
      previous
        ? {
            ...previous,
            answers: previous.answers.map((answer, slot) => (slot === index ? value : answer)),
            errors: previous.errors.map((error, slot) => (slot === index ? false : error)),
          }
        : previous,
    );
  };
  // Il verdetto nasce dal click, non dalla digitazione: validare a ogni battuta
  // dipingerebbe di rosso un campo mentre l'utente scrive la prima lettera.
  const verify = async () => {
    if (!challenge) return;
    const errors = challenge.positions.map(
      (position, index) => !matchesWord(challenge.answers[index] ?? "", words[position] ?? ""),
    );
    if (errors.some(Boolean)) {
      setChallenge({ ...challenge, errors });
      return;
    }
    await store.confirmBackup();
    setChallenge(undefined);
    setBackupStep("hidden");
  };
  const wrongCount = challenge?.errors.filter(Boolean).length ?? 0;
  return (
    <section className="stack settings-stack">
      <PageTitle
        eyebrow="Profilo locale"
        title={store.profile!.displayName}
        text={`Ruolo: ${store.profile!.role === "member" ? "atleta" : "coach"}`}
      />
      <section className="settings-card">
        <h2>Recupero del profilo</h2>
        <p>Queste parole ricreano la tua identità. Non inviarle a nessuno.</p>
        {backupStep === "hidden" ? (
          <>
            <button className="secondary" onClick={() => setBackupStep("revealed")}>
              {store.backupConfirmed ? "Mostra le 12 parole" : "Salva le 12 parole"}
            </button>
            {store.backupConfirmed ? <p className="hint">Backup confermato.</p> : null}
          </>
        ) : null}
        {/* In verifica la lista non viene renderizzata affatto: nasconderla con
            il CSS lascerebbe le parole nel DOM, leggibili da strumenti e screen
            reader, e la verifica tornerebbe finta con in più l'illusione di non
            esserlo. */}
        {backupStep === "revealed" ? (
          <>
            <ol className="mnemonic">
              {words.map((word, index) => (
                <li key={index}>{word}</li>
              ))}
            </ol>
            <div className="button-row">
              {!store.backupConfirmed ? (
                <button className="primary" onClick={startVerification}>
                  Le ho annotate
                </button>
              ) : null}
              <button className="secondary" onClick={() => setBackupStep("hidden")}>
                Nascondi
              </button>
            </div>
          </>
        ) : null}
        {backupStep === "verifying" && challenge ? (
          <div className="word-check">
            <p>Riscrivi le parole che ti chiediamo. La frase resta nascosta.</p>
            {wrongCount > 0 ? (
              <p className="field-error" role="alert">
                {wrongCount === 1
                  ? "Una parola non corrisponde. Controlla il campo segnalato."
                  : `${wrongCount} parole non corrispondono. Controlla i campi segnalati.`}
              </p>
            ) : null}
            {challenge.positions.map((position, index) => {
              const wrong = challenge.errors[index] === true;
              const errorId = `word-check-error-${position}`;
              return (
                <div className="word-check-field" key={position}>
                  <label>
                    Parola {position + 1}
                    {/* La tolleranza alle maiuscole cura il sintomo, questi
                        attributi curano la causa: la correzione automatica non
                        cambia il maiuscolo, sostituisce la parola. */}
                    <input
                      value={challenge.answers[index] ?? ""}
                      onChange={(event) => answerWord(index, event.target.value)}
                      autoCapitalize="none"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck={false}
                      aria-invalid={wrong || undefined}
                      aria-describedby={wrong ? errorId : undefined}
                      className={wrong ? "input-error" : undefined}
                    />
                  </label>
                  {wrong ? (
                    <span className="field-error" id={errorId}>
                      Non è la parola {position + 1} della tua frase
                    </span>
                  ) : null}
                </div>
              );
            })}
            <div className="button-row">
              <button className="primary" onClick={() => void verify()}>
                Conferma backup
              </button>
              <button className="text-button" onClick={reviewPhrase}>
                Rivedi le 12 parole
              </button>
            </div>
          </div>
        ) : null}
      </section>
      {/* Due domande diverse, due schermate: «chi c'è dentro» e «che inviti ho
          in giro». La scheda mostrava gli invii, e chi guida una palestra ha
          bisogno di sapere chi c'è. */}
      {store.profile!.role !== "member" ? (
        // Gli inviti sono del solo proprietario; l'elenco di chi c'è lo legge
        // anche il coach, che con quelle persone ci lavora.
        invitesOpen && store.profile!.role === "owner" ? (
          <PendingInvites onBack={() => setInvitesOpen(false)} />
        ) : (
          <AccessOverview onOpenInvites={() => setInvitesOpen(true)} />
        )
      ) : null}
      <section className="settings-card">
        <h2>Storage del telefono</h2>
        <p>
          I dati restano disponibili offline. Il sistema operativo può comunque rimuoverli in
          condizioni estreme.
        </p>
        <button
          className="secondary"
          onClick={() =>
            void requestPersistentStorage()
              .then(() => storageDiagnostics())
              .then(setDiagnostics)
          }
        >
          Proteggi lo storage
        </button>
        {diagnostics ? (
          <p className="hint">
            Persistente: {diagnostics.persisted ? "sì" : "non garantito"} · Usati{" "}
            {formatBytes(diagnostics.usage)}
          </p>
        ) : null}
      </section>
      <section className="settings-card danger-zone">
        <h2>Rimuovi profilo dal dispositivo</h2>
        <p>
          Cancella identità, chiavi, prenotazioni e modifiche locali da questo browser. Non revoca
          il profilo dagli altri dispositivi. La copia cifrata di recupero resta sul server finché
          non chiedi di cancellarla: puoi farlo nella stessa finestra.
        </p>
        {!store.backupConfirmed ? (
          <p className="field-error">Prima salva e conferma le 12 parole.</p>
        ) : (
          <button className="secondary danger" onClick={() => setRemoving(true)}>
            Rimuovi profilo
          </button>
        )}
      </section>
      {/* Distinto da «Rimuovi profilo», e non un suo parente povero: quello è
          l'uscita definitiva e pretende le 12 parole a riprova che si sappia
          rientrare; questo è il ritorno al punto di partenza su *questo*
          dispositivo, e deve restare raggiungibile anche quando il dispositivo
          è messo male — che è l'unico momento in cui serve davvero. */}
      <section className="settings-card danger-zone">
        <h2>Azzera questo dispositivo</h2>
        <p>
          Riporta questo browser a com’era appena installato: identità, chiavi, documenti,
          cronologia locale e <strong>le modifiche non ancora inviate</strong>. La copia cifrata
          custodita dal relay resta al suo posto, quindi con le 12 parole puoi rientrare — o
          riprovare il recupero — da uno stato pulito.
        </p>
        <p className="hint">
          Serve quando questo dispositivo non riesce a riallinearsi: dopo l’azzeramento riparte da
          ciò che c’è sul server, non da ciò che si era portato dietro.
        </p>
        <button className="secondary danger" onClick={() => setWiping(true)}>
          Azzera questo dispositivo
        </button>
      </section>
      <BuildFooter />
      {removing ? <RemoveProfileDialog onClose={() => setRemoving(false)} /> : null}
      {wiping ? <WipeDeviceDialog onClose={() => setWiping(false)} /> : null}
    </section>
  );
}

/**
 * L'ultima riga del Profilo, e la sola che parli del software invece che della
 * palestra: quale delle due PWA sta girando su questo telefono, di quando e' la
 * build, e quale dei due relay le ha risposto. Sta in fondo perche' e' la
 * domanda che ci si fa a rilascio appena fatto — non tutti i giorni.
 */
function BuildFooter() {
  const sync = useSyncStatus();
  return (
    <footer className="build-footer">
      <p>{formatBuildLine()}</p>
      <p>{formatRelayLine(sync.relay)}</p>
    </footer>
  );
}

/**
 * Un invito coach e un invito atleta non sono la stessa cosa e non devono
 * avere lo stesso aspetto: il badge e' la distinzione a colpo d'occhio, non
 * una parola in grigio.
 */
function RoleBadge({ role }: { role: Role }) {
  const label = role === "owner" ? "Proprietario" : role === "coach" ? "Coach" : "Atleta";
  return <span className={`role-badge role-badge-${role}`}>{label}</span>;
}

function shortInstant(value: string, withTime = false): string {
  return new Date(value).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

/**
 * Chi ha accesso — la principale. L'elenco si costruisce sui `peers`, non su
 * `spentInvites`: quelli si potano dopo trenta giorni, e un elenco costruito
 * su di essi si svuoterebbe da solo dopo un mese. Il nome arriva dalla
 * `MemberSchedule` che ogni persona pubblica appena entra; l'attribuzione
 * «entrato il…» arriva da `spentInvites` finché c'e', e quando non c'e' piu'
 * si tace, non si mostra vuota.
 */
function AccessOverview({ onOpenInvites }: { onOpenInvites: () => void }) {
  const store = useAppStore();
  const profile = store.profile!;
  const people = profile.peers.filter((peer) => peer.id !== store.identity!.id);
  const nameOf = (peerId: string) =>
    store.schedules.find((schedule) => schedule.memberId === peerId)?.displayName;
  const sinceOf = (peerId: string) =>
    profile.spentInvites?.find((entry) => entry.consumedBy === peerId)?.recordedAt;
  return (
    <section className="settings-card">
      <h2>Chi ha accesso</h2>
      <p>Le persone entrate in questa palestra, con il loro ruolo.</p>
      {people.length === 0 ? (
        <p className="hint">Non è ancora entrato nessuno: crea un invito e condividilo.</p>
      ) : (
        <div className="coach-peers">
          {people.map((peer) => {
            const name = nameOf(peer.id);
            const since = sinceOf(peer.id);
            return (
              <div key={peer.id}>
                <div>
                  {/* Il nome, non l'identificativo: `person_rk4SGfd…` non
                      significa niente per un allenatore. Chi e' entrato ma non
                      si e' ancora collegato non ha ancora un nome, e la riga
                      lo dice. */}
                  <strong>{name ?? "In attesa del primo collegamento"}</strong>
                  <p className="hint">
                    <RoleBadge role={peer.role} />
                    {since ? <> · entrato il {shortInstant(since)}</> : null}
                  </p>
                </div>
                {/* `role.revoked` e' consentito al solo proprietario, e il
                    vincolo e' nel core: a chi non puo' usarlo il comando non
                    si mostra, invece di mostrarsi e poi fallire. */}
                {profile.role === "owner" ? (
                  <button
                    className="text-button danger"
                    onClick={() => void store.revokePeer(peer.id)}
                  >
                    Revoca l’accesso
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {/* Chi fa entrare qualcuno in palestra è chi la palestra ce l'ha: il
          comando non si mostra a chi il core rifiuterebbe, e la riga dice
          perché, invece di lasciare un'assenza da interpretare. */}
      {profile.role === "owner" ? (
        <div className="button-row">
          <button className="secondary" onClick={onOpenInvites}>
            Invito a coach e atleti{store.invites.length > 0 ? ` (${store.invites.length})` : ""}
          </button>
        </div>
      ) : (
        <p className="hint">Gli inviti li crea il proprietario della palestra.</p>
      )}
    </section>
  );
}

/**
 * Inviti in sospeso — la secondaria. Contiene gli inviti fatti e non ancora
 * spesi: ruolo, creato il, scade il, il QR e il link ancora mostrabili, e
 * Ritira. Si apre dal solo proprietario, che è anche l'unico a poterli fare:
 * crearli e ritirarli sono lo stesso comando visto da due lati.
 */
function PendingInvites({ onBack }: { onBack: () => void }) {
  const store = useAppStore();
  const [inviteUrl, setInviteUrl] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [inviteError, setInviteError] = useState("");
  const qrBox = useRef<HTMLDivElement>(null);
  const inviteField = useRef<HTMLTextAreaElement>(null);
  const createLink = async (role: "member" | "coach") => {
    setInviteError("");
    try {
      const issued = await store.makeInvite(role);
      setInviteUrl(issued.url ?? "");
      // Il messaggio parla della copia precedente: tenerlo direbbe che il
      // nuovo invito è già negli appunti, e non lo è.
      setShareNote("");
    } catch (reason) {
      setInviteError(reason instanceof Error ? reason.message : "Invito non creato");
    }
  };
  const shareLink = async () => {
    setShareNote(linkNote(await copyInviteLink(inviteUrl, inviteField.current)));
  };
  const shareQr = async () => {
    setShareNote(qrNote(await copyInviteQr(qrBox.current?.querySelector("svg") ?? null)));
  };
  return (
    <section className="settings-card">
      <h2>Inviti in sospeso</h2>
      <p>
        Gli inviti che hai fatto tu e che nessuno ha ancora usato. Ogni link vale una volta sola e
        scade dopo 14 giorni; puoi ritirarlo quando vuoi, e da quel momento non si apre più.
      </p>
      <div className="button-row">
        <button className="secondary" onClick={() => void createLink("member")}>
          Invita atleta
        </button>
        <button className="secondary" onClick={() => void createLink("coach")}>
          Invita coach
        </button>
      </div>
      {/* Un invito coach apre la vista nominativa su presenze e pianificazioni
          di tutti: due settimane sono difendibili solo se lo si dice. */}
      <p className="hint">
        Un invito coach dà accesso a presenze e pianificazioni di tutti: condividilo solo con chi
        deve allenare, e ritiralo se non serve più.
      </p>
      {inviteError ? (
        <p className="field-error" role="alert">
          {inviteError}
        </p>
      ) : null}
      {inviteUrl ? (
        <div className="invite-box">
          <div className="invite-qr" ref={qrBox}>
            {/* Il chunk di `qrcode.react` parte quando l'invito esiste, non
                al primo avvio; il confine e' locale cosi' il resto della
                scheda — link e Copia — non aspetta il QR. */}
            <Suspense fallback={null}>
              <InviteQr value={inviteUrl} size={240} />
            </Suspense>
          </div>
          {/* Inquadrare il QR richiede di essere nella stessa stanza: chi
              invita per messaggio passa da qui, quindi i due Copia prendono
              tutta la larghezza invece di stare in un angolo. */}
          <div className="button-row invite-share">
            <button className="primary" onClick={() => void shareLink()}>
              Copia link
            </button>
            <button className="secondary" onClick={() => void shareQr()}>
              Copia QR
            </button>
          </div>
          {/* La regione vive sempre: montarla insieme al messaggio la
              lascerebbe muta per gli screen reader. */}
          <p className="hint invite-share-note" role="status">
            {shareNote}
          </p>
          <label>
            Link invito
            <textarea
              ref={inviteField}
              readOnly
              value={inviteUrl}
              rows={2}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
        </div>
      ) : null}
      {/* Un invito che non si vede non si può ritirare: finché restava
          invisibile, un link finito in una chat di gruppo era valido fino
          alla scadenza e non c'era comando che lo fermasse. */}
      {store.invites.length > 0 ? (
        <div className="coach-peers">
          {store.invites.map((invite) => (
            <div key={invite.id}>
              <span>
                <RoleBadge role={invite.role} /> · creato il {shortInstant(invite.issuedAt)} · scade
                il {shortInstant(invite.expiresAt, true)}
              </span>
              <span className="button-row">
                {invite.url ? (
                  <button
                    className="text-button"
                    onClick={() => {
                      setInviteUrl(invite.url ?? "");
                      setShareNote("");
                      setInviteError("");
                    }}
                  >
                    Mostra
                  </button>
                ) : null}
                <button
                  className="text-button danger"
                  onClick={() => {
                    if (invite.url === inviteUrl) setInviteUrl("");
                    void store.revokeInvite(invite.id);
                  }}
                >
                  Ritira
                </button>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="hint">Nessun invito in sospeso.</p>
      )}
      <button className="text-button" onClick={onBack}>
        ← Torna a «Chi ha accesso»
      </button>
    </section>
  );
}

function RemoveProfileDialog({ onClose }: { onClose: () => void }) {
  const store = useAppStore();
  const [phrase, setPhrase] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [removeRemoteBackup, setRemoveRemoteBackup] = useState(false);
  const [error, setError] = useState("");
  const remove = async () => {
    setError("");
    try {
      await store.removeProfile(phrase, confirmation, removeRemoteBackup);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Rimozione non riuscita");
    }
  };
  return (
    <Modal onClose={onClose} labelledBy="remove-profile-title">
      <p className="eyebrow">Azione irreversibile su questo dispositivo</p>
      <h2 id="remove-profile-title">Rimuovere davvero il profilo?</h2>
      <p>
        Per rientrare bastano queste 12 parole: palestra, ruolo e pianificazione tornano dalla copia
        cifrata custodita dal relay. Solo se quella copia non fosse disponibile servirà un nuovo
        invito della palestra.
      </p>
      <label>
        Inserisci le tue 12 parole
        <textarea rows={4} value={phrase} onChange={(event) => setPhrase(event.target.value)} />
      </label>
      <label>
        Scrivi RIMUOVI
        <input
          autoComplete="off"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
        />
      </label>
      <label className="inline-check">
        <input
          type="checkbox"
          checked={removeRemoteBackup}
          onChange={(event) => setRemoveRemoteBackup(event.target.checked)}
        />
        Cancella anche la copia di recupero custodita dal server
      </label>
      <p className="hint">
        {removeRemoteBackup
          ? "Le 12 parole non basteranno più a rientrare: servirà un nuovo invito della palestra."
          : "Senza la spunta, la copia cifrata resta sul server ed è ciò che ti permette di rientrare."}
      </p>
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      <button
        className="primary destructive"
        disabled={phrase.trim().split(/\s+/).length !== 12 || confirmation !== "RIMUOVI"}
        onClick={() => void remove()}
      >
        Cancella dati da questo dispositivo
      </button>
      <button className="text-button" onClick={onClose}>
        Annulla
      </button>
    </Modal>
  );
}

/**
 * La conferma è una parola, non le 12: chi azzera per riprovare il recupero
 * spesso non ha nulla di sensato da digitare — è precisamente la ragione per cui
 * sta azzerando. Il prezzo di questa porta più bassa è dirlo chiaro: che cosa se
 * ne va, e che cosa resta a permettere il rientro.
 */
function WipeDeviceDialog({ onClose }: { onClose: () => void }) {
  const store = useAppStore();
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useSyncStatus().pending;
  const wipe = async () => {
    setError("");
    setBusy(true);
    try {
      await store.wipeDevice(confirmation);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Azzeramento non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} labelledBy="wipe-device-title">
      <p className="eyebrow">Azione irreversibile su questo dispositivo</p>
      <h2 id="wipe-device-title">Azzerare questo dispositivo?</h2>
      <p>
        Vengono cancellati identità, chiavi, palestra, pianificazioni, cronologia locale e la coda
        delle modifiche non ancora inviate. Gli altri dispositivi non vengono toccati.
      </p>
      {pending > 0 ? (
        <p className="field-error">
          {pending === 1
            ? "C’è 1 modifica non ancora inviata: andrà persa."
            : `Ci sono ${pending} modifiche non ancora inviate: andranno perse.`}
        </p>
      ) : null}
      <p className="hint">
        {store.backupConfirmed
          ? "La copia cifrata sul relay resta: con le 12 parole rientri da qui o da un altro dispositivo."
          : "Non hai ancora confermato le 12 parole su questo dispositivo: se non le hai annotate altrove, non potrai rientrare. La copia sul relay resta, ma senza le parole non si apre."}
      </p>
      <label>
        Scrivi AZZERA
        <input
          autoComplete="off"
          autoFocus
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
        />
      </label>
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      <button
        className="primary destructive"
        disabled={confirmation.trim().toUpperCase() !== "AZZERA" || busy}
        onClick={() => void wipe()}
      >
        {busy ? "Azzeramento…" : "Azzera adesso"}
      </button>
      <button className="text-button" onClick={onClose}>
        Annulla
      </button>
    </Modal>
  );
}

function SyncErrorDialog({ onClose }: { onClose: () => void }) {
  const sync = useSyncStatus();
  const { reconnect } = useAppStore();
  const detail = syncErrorDetail(sync);
  // L'errore puo' decadere mentre la finestra e' aperta: una causa non piu' vera
  // e' peggio di nessuna causa.
  useEffect(() => {
    if (sync.state !== "error") onClose();
  }, [onClose, sync.state]);
  return (
    <Modal onClose={onClose} labelledBy="sync-error-title">
      <p className="eyebrow">Stato della sincronizzazione</p>
      <h2 id="sync-error-title">Errore di sincronizzazione</h2>
      <p>{detail.cause}</p>
      <p className="sync-error-context">
        {detail.queue} {detail.lastContact}
      </p>
      <button
        className="primary"
        onClick={() => {
          onClose();
          reconnect();
        }}
      >
        Riprova ora
      </button>
      <button className="text-button" onClick={onClose}>
        Chiudi
      </button>
    </Modal>
  );
}

function SyncBadge() {
  const sync = useSyncStatus();
  const { reconnect } = useAppStore();
  const [detailOpen, setDetailOpen] = useState(false);
  const label = syncBadgeLabel(sync);
  const attention = syncNeedsAttention(sync);
  const className = ["sync-badge", `sync-${sync.state}`, attention ? "sync-attention" : ""]
    .filter(Boolean)
    .join(" ");
  // Solo l'errore porta qualcosa che l'etichetta non dice gia': altrove il tocco
  // resta il tentativo immediato, senza una finestra da chiudere prima.
  const isError = sync.state === "error";
  return (
    <>
      <button
        className={className}
        disabled={sync.state === "connecting"}
        onClick={isError ? () => setDetailOpen(true) : reconnect}
        aria-label={
          isError
            ? `${label}. Tocca per leggere la causa`
            : `${label}. Tocca per contattare il relay`
        }
      >
        {isError ? <span aria-hidden="true">⚠ </span> : null}
        {label}
      </button>
      {detailOpen ? <SyncErrorDialog onClose={() => setDetailOpen(false)} /> : null}
    </>
  );
}

function SyncNotice() {
  const sync = useSyncStatus();
  const explanation = syncExplanation(sync);
  if (!explanation) return null;
  return (
    <p className={`notice sync-notice${syncNeedsAttention(sync) ? " sync-notice-attention" : ""}`}>
      {explanation}
    </p>
  );
}

function InstallAndUpdate() {
  const [installEvent, setInstallEvent] = useState<Event & { prompt: () => Promise<void> }>();
  const [iosHintDismissed, setIosHintDismissed] = useState(false);
  const { needRefresh, updateServiceWorker } = useRegisterSW();
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    globalThis.matchMedia?.("(display-mode: standalone)").matches ||
    ("standalone" in navigator &&
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
  useEffect(() => {
    const listener = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as Event & { prompt: () => Promise<void> });
    };
    globalThis.addEventListener("beforeinstallprompt", listener);
    return () => globalThis.removeEventListener("beforeinstallprompt", listener);
  }, []);
  return (
    <>
      {installEvent ? (
        <div className="action-toast">
          <span>Installa l’app sul telefono</span>
          <button onClick={() => void installEvent.prompt().then(() => setInstallEvent(undefined))}>
            Installa
          </button>
        </div>
      ) : null}
      {isIos && !isStandalone && !installEvent && !iosHintDismissed ? (
        <div className="action-toast" role="status">
          <span>Per installare: tocca Condividi, poi “Aggiungi alla schermata Home”.</span>
          <button onClick={() => setIosHintDismissed(true)}>Capito</button>
        </div>
      ) : null}
      {needRefresh[0] ? (
        <div className="action-toast">
          <span>Aggiornamento pronto. I dati locali restano al sicuro.</span>
          <button onClick={() => void updateServiceWorker(true)}>Aggiorna</button>
        </div>
      ) : null}
    </>
  );
}

function NavButton({
  active,
  icon,
  children,
  onClick,
}: {
  active: boolean;
  icon: string;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className={active ? "active" : ""}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <span aria-hidden="true">{icon}</span>
      {children}
    </button>
  );
}
function CenteredCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="centered">
      <section className="centered-card">
        <img src="/icon.svg" alt="" />
        <h1>{title}</h1>
        {children}
      </section>
    </main>
  );
}
